import React, { useEffect, useRef, useState } from 'react';
import { RallyHitQuality } from '../../graphql-operations';

type Props = {
  disabled?: boolean;
  onHit: (quality: RallyHitQuality) => void;
};

const SWEEP_MS = 1_700;
const SPOT_WIDTH = 14;

const nextSpot = () => 15 + Math.random() * (83 - SPOT_WIDTH - 15);

export default function TimingControl({ disabled, onHit }: Props) {
  const marker = useRef<HTMLDivElement>(null);
  const markerPosition = useRef(0);
  const sweepStarted = useRef(performance.now());
  const [spot, setSpot] = useState(nextSpot);
  const [feedback, setFeedback] = useState('TRYCK!');

  useEffect(() => {
    let frame = 0;
    const animate = (now: number) => {
      const phase = ((now - sweepStarted.current) % (SWEEP_MS * 2)) / SWEEP_MS;
      const position = phase <= 1 ? phase * 100 : (2 - phase) * 100;
      markerPosition.current = position;
      if (marker.current) marker.current.style.left = `${position}%`;
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  const hit = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (disabled) return;
    const center = spot + SPOT_WIDTH / 2;
    const distance = Math.abs(markerPosition.current - center);
    const quality =
      distance <= SPOT_WIDTH * 0.22
        ? RallyHitQuality.Excellent
        : distance <= SPOT_WIDTH * 0.58
        ? RallyHitQuality.Good
        : RallyHitQuality.Poor;
    setFeedback(
      quality === RallyHitQuality.Excellent
        ? 'PERFEKT! +4'
        : quality === RallyHitQuality.Good
        ? 'BRA! +2'
        : 'KÄMPA! +1'
    );
    onHit(quality);
    setSpot(nextSpot());
    sweepStarted.current = performance.now();
    window.setTimeout(() => setFeedback('TRYCK!'), 550);
  };

  return (
    <div className="rally-control" aria-label="Tajmningskontroll">
      <div className="rally-feedback" aria-live="polite">
        {feedback}
      </div>
      <button
        className="rally-meter"
        type="button"
        onPointerDown={hit}
        disabled={disabled}
        aria-label="Tryck när markören är i det ljusa fältet"
      >
        <span
          className="rally-sweet-spot"
          style={{ left: `${spot}%`, width: `${SPOT_WIDTH}%` }}
        />
        <span className="rally-meter-marker" ref={marker} />
      </button>
    </div>
  );
}
